import { useState } from 'react';
import { Check, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { getSettings, saveSettings, sha256, StoreSettings } from '@/lib/store';
import { useToast } from '@/hooks/useToast';

export function AdminSettings() {
  const [settings, setSettings] = useState<StoreSettings>(() => getSettings());
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const { toast } = useToast();

  function handleChange(field: keyof StoreSettings, value: string) {
    setSettings((prev) => ({ ...prev, [field]: value }));
  }

  async function saveGeneral() {
    setSaving(true);
    saveSettings({
      storeName: settings.storeName,
      storeEmail: settings.storeEmail,
      storeCurrency: settings.storeCurrency,
    });
    setSaving(false);
    toast({ title: 'Store settings saved!' });
  }

  async function savePaymentSettings() {
    setSaving(true);
    saveSettings({
      paypalClientId: settings.paypalClientId,
      paypalMode: settings.paypalMode,
      squareAppId: settings.squareAppId,
      squareLocationId: settings.squareLocationId,
      squareMode: settings.squareMode,
      checkoutProvider: settings.checkoutProvider,
    });
    setSaving(false);
    toast({ title: 'Payment settings saved!' });
  }

  async function handlePasswordChange() {
    if (newPassword !== confirmPassword) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: 'Password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    setSavingPw(true);
    const hash = await sha256(newPassword);
    saveSettings({ adminPasswordHash: hash });
    setSettings(getSettings());
    setNewPassword('');
    setConfirmPassword('');
    setSavingPw(false);
    toast({ title: 'Password updated successfully!' });
  }

  const paypalConfigured = !!settings.paypalClientId;
  const squareConfigured = !!(settings.squareAppId && settings.squareLocationId);

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-stone-400 text-sm mt-1">Configure your store, payments, and admin access.</p>
      </div>

      {/* Store Info */}
      <Card className="bg-[hsl(25,20%,12%)] border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-base">Store Information</CardTitle>
          <CardDescription className="text-stone-400">Basic details about your store.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-stone-300">Store Name</Label>
            <Input
              value={settings.storeName}
              onChange={(e) => handleChange('storeName', e.target.value)}
              className="bg-[hsl(25,20%,8%)] border-white/10 text-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-stone-300">Contact Email</Label>
            <Input
              type="email"
              value={settings.storeEmail}
              onChange={(e) => handleChange('storeEmail', e.target.value)}
              className="bg-[hsl(25,20%,8%)] border-white/10 text-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-stone-300">Currency</Label>
            <select
              value={settings.storeCurrency}
              onChange={(e) => handleChange('storeCurrency', e.target.value)}
              className="w-full h-9 rounded-md border border-white/10 bg-[hsl(25,20%,8%)] text-white px-3 text-sm focus:outline-none focus:ring-1 focus:ring-amber-600"
            >
              <option value="USD">USD — US Dollar</option>
              <option value="CAD">CAD — Canadian Dollar</option>
              <option value="EUR">EUR — Euro</option>
              <option value="GBP">GBP — British Pound</option>
            </select>
          </div>
          <Button onClick={saveGeneral} disabled={saving} className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm">
            <Check className="h-4 w-4 mr-1.5" /> Save Store Settings
          </Button>
        </CardContent>
      </Card>

      {/* Payment Providers */}
      <Card className="bg-[hsl(25,20%,12%)] border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-base">Payment Providers</CardTitle>
          <CardDescription className="text-stone-400">
            Connect PayPal and/or Square to enable checkout. Your credentials are stored locally in the browser.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Checkout Provider selector */}
          <div className="space-y-2">
            <Label className="text-stone-300">Active Checkout Provider</Label>
            <div className="grid grid-cols-3 gap-2">
              {(['paypal', 'square', 'both'] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleChange('checkoutProvider', opt)}
                  className={`py-2 px-3 rounded-md text-sm font-medium border transition-colors ${
                    settings.checkoutProvider === opt
                      ? 'border-amber-600 bg-amber-700/20 text-amber-400'
                      : 'border-white/10 bg-transparent text-stone-400 hover:text-white hover:border-white/30'
                  }`}
                >
                  {opt === 'both' ? 'Both' : opt === 'paypal' ? 'PayPal' : 'Square'}
                </button>
              ))}
            </div>
          </div>

          <Separator className="bg-white/10" />

          {/* PayPal */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 bg-[#003087] rounded-sm flex items-center justify-center">
                  <span className="text-white text-xs font-bold">P</span>
                </div>
                <span className="text-white font-medium">PayPal</span>
              </div>
              <div className="flex items-center gap-2">
                {paypalConfigured && <Badge className="bg-green-900/40 text-green-400 border-0 text-xs">Configured</Badge>}
                <a
                  href="https://developer.paypal.com/dashboard/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-stone-500 hover:text-amber-400 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            <div className="space-y-3 pl-8">
              <div className="space-y-1.5">
                <Label className="text-stone-400 text-xs">Client ID</Label>
                <Input
                  value={settings.paypalClientId}
                  onChange={(e) => handleChange('paypalClientId', e.target.value)}
                  placeholder="AYour-PayPal-Client-Id…"
                  className="bg-[hsl(25,20%,8%)] border-white/10 text-white font-mono text-xs placeholder:text-stone-700"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-stone-400 text-xs">Mode</Label>
                <div className="flex gap-2">
                  {(['sandbox', 'live'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => handleChange('paypalMode', m)}
                      className={`flex-1 py-1.5 text-xs rounded border transition-colors capitalize ${
                        settings.paypalMode === m
                          ? 'border-amber-600 bg-amber-700/20 text-amber-400'
                          : 'border-white/10 text-stone-500 hover:text-white'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-stone-600 text-xs">
                Get your Client ID from the{' '}
                <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline">
                  PayPal Developer Dashboard
                </a>
                . Use Sandbox for testing, Live for real transactions.
              </p>
            </div>
          </div>

          <Separator className="bg-white/10" />

          {/* Square */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 bg-black border border-white/20 rounded-sm flex items-center justify-center">
                  <span className="text-white text-xs font-bold">■</span>
                </div>
                <span className="text-white font-medium">Square</span>
              </div>
              <div className="flex items-center gap-2">
                {squareConfigured && <Badge className="bg-green-900/40 text-green-400 border-0 text-xs">Configured</Badge>}
                <a
                  href="https://developer.squareup.com/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-stone-500 hover:text-amber-400 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            <div className="space-y-3 pl-8">
              <div className="space-y-1.5">
                <Label className="text-stone-400 text-xs">Application ID</Label>
                <Input
                  value={settings.squareAppId}
                  onChange={(e) => handleChange('squareAppId', e.target.value)}
                  placeholder="sq0idp-…"
                  className="bg-[hsl(25,20%,8%)] border-white/10 text-white font-mono text-xs placeholder:text-stone-700"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-stone-400 text-xs">Location ID</Label>
                <Input
                  value={settings.squareLocationId}
                  onChange={(e) => handleChange('squareLocationId', e.target.value)}
                  placeholder="L…"
                  className="bg-[hsl(25,20%,8%)] border-white/10 text-white font-mono text-xs placeholder:text-stone-700"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-stone-400 text-xs">Mode</Label>
                <div className="flex gap-2">
                  {(['sandbox', 'production'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => handleChange('squareMode', m)}
                      className={`flex-1 py-1.5 text-xs rounded border transition-colors capitalize ${
                        settings.squareMode === m
                          ? 'border-amber-600 bg-amber-700/20 text-amber-400'
                          : 'border-white/10 text-stone-500 hover:text-white'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-stone-600 text-xs">
                Get your App ID and Location ID from the{' '}
                <a href="https://developer.squareup.com/apps" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline">
                  Square Developer Console
                </a>.
              </p>
            </div>
          </div>

          <Separator className="bg-white/10" />

          <Button onClick={savePaymentSettings} className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm">
            <Check className="h-4 w-4 mr-1.5" /> Save Payment Settings
          </Button>
        </CardContent>
      </Card>

      {/* Admin Password */}
      <Card className="bg-[hsl(25,20%,12%)] border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-base">Change Admin Password</CardTitle>
          <CardDescription className="text-stone-400">
            Set a secure password to protect admin access. Minimum 8 characters.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-stone-300">New Password</Label>
            <div className="relative">
              <Input
                type={showPw ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min. 8 characters"
                className="bg-[hsl(25,20%,8%)] border-white/10 text-white pr-10 placeholder:text-stone-600"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-stone-300">Confirm New Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              className="bg-[hsl(25,20%,8%)] border-white/10 text-white placeholder:text-stone-600"
            />
          </div>
          <Button
            onClick={handlePasswordChange}
            disabled={savingPw || !newPassword || !confirmPassword}
            className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm"
          >
            <Check className="h-4 w-4 mr-1.5" />
            {savingPw ? 'Updating…' : 'Update Password'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default AdminSettings;
